/**
 * KollectiviaMark — signature visuelle Kollectivia.
 *
 * **Source de vérité** : `docs/branding/kollectivia-mark-source.svg`. Géométrie
 * et couleurs alignées au pixel près sur ce fichier (viewBox 512×512, 6 nœuds,
 * 6 segments, gradients linéaires + glow filter).
 *
 * Topologie (6 nœuds, 6 segments) :
 *   - N0 (120, 62)   — top stem (haut-gauche)
 *   - N1 (120, 238)  — intersection mid-stem (gauche-milieu)
 *   - N2 (120, 438)  — bottom stem (bas-gauche)
 *   - N3 (255, 238)  — mid-arm node (centre, point pivot vers les 2 tips)
 *   - N4 (390, 62)   — upper-arm tip (haut-droite)
 *   - N5 (385, 438)  — lower-arm tip (bas-droite)
 *
 * Segments :
 *   - N0 → N2 : stem vertical complet
 *   - N1 → N4 : intersection → upper-tip (lineGradient2)
 *   - N1 → N5 : intersection → lower-tip (lineGradient1)
 *   - N2 → N3 : bottom-left → mid-arm node (cyan-blue)
 *   - N3 → N4 : mid-arm → upper-tip (cyan-light)
 *   - N3 → N5 : mid-arm → lower-tip (blue-deep)
 *
 * Le glow filter (feGaussianBlur stdDeviation=6) ajoute un halo subtil — désactivable
 * via prop `glow={false}` pour favicon ou contextes perf-sensibles.
 *
 * `useId()` scope les `<defs>` IDs (gradients + filter) par instance de component
 * pour éviter les conflits DOM si plusieurs marks coexistent sur la même page.
 *
 * Variantes :
 *   - `variant="color"` (défaut) : palette source officielle.
 *   - `variant="mono"` : uniformisé brand-blue (réservé surfaces sombres / dark mode).
 *
 * Accessibilité :
 *   - Si `ariaLabel` fourni → `role="img"` + label (ex: "Kollectivia").
 *   - Sinon → `aria-hidden="true"` (icône décorative à côté d'un wordmark déjà textuel).
 *
 * 9.P.1b — Animation runtime (prop `animate`) :
 *   - "entrance" → 6 lignes draw-line staggered (delay 0/0.15/.../0.75s, 1.4s
 *     ease) + 6 nodes pop staggered (delay 0.9/.../1.4s, 0.55s spring). Joue
 *     une fois sur mount.
 *   - "idle" → svg float ±6px 5s infinite + per-node breathe staggered 2.4-3.9s
 *     infinite.
 *   - "all" → entrance + idle (breathe + float commencent en même temps que
 *     l'entrance via composition CSS shorthand ; float a delay 1s pour fluidité).
 *   - undefined → fallback static (compat 9.P.1 et antérieur, zéro classe
 *     d'animation appliquée, mark visible plein).
 *   - prefers-reduced-motion: reduce → toutes les classes sont gated par un
 *     @media en index.css → fallback static naturel, sans configuration JS.
 *
 * Source canonique animation : `docs/branding/kollectivia-mark-animation-reference.html`
 * blocs A+B uniquement. Blocs C/D (packets/hover JS) hors scope applicatif.
 */

import { useId, type SVGProps, type CSSProperties } from "react";

// Positions exactes du mark source (viewBox 512×512).
const NODES = [
  { x: 120, y: 62,  r: 28, fill: "#070B73" }, // N0 — top stem (deep navy)
  { x: 120, y: 238, r: 34, fill: "#1D7BEF" }, // N1 — intersection (anchor, plus gros)
  { x: 120, y: 438, r: 32, fill: "#246EEB" }, // N2 — bottom stem
  { x: 255, y: 238, r: 24, fill: "#22AEEB" }, // N3 — mid-arm (centre, plus petit)
  { x: 390, y: 62,  r: 30, fill: "#7B32EA" }, // N4 — upper-arm tip (violet)
  { x: 385, y: 438, r: 30, fill: "#3268E9" }, // N5 — lower-arm tip
] as const;

// 6 segments avec couleurs/gradients du source.
type LineSpec =
  | { from: number; to: number; stroke: string }
  | { from: number; to: number; gradient: "g1" | "g2" };

const LINES: ReadonlyArray<LineSpec> = [
  { from: 0, to: 2, stroke: "#306FE8" },     // N0→N2 : stem complet (passe par N1)
  { from: 1, to: 4, gradient: "g2" },         // N1→N4 : intersect → upper-tip
  { from: 1, to: 5, gradient: "g1" },         // N1→N5 : intersect → lower-tip
  { from: 2, to: 3, stroke: "#2D9CEB" },     // N2→N3 : bottom → mid-arm
  { from: 3, to: 4, stroke: "#42AEEB" },     // N3→N4 : mid-arm → upper-tip
  { from: 3, to: 5, stroke: "#2B64E8" },     // N3→N5 : mid-arm → lower-tip
];

const MONO_FILL = "#2563EB";
const MONO_STROKE = "#2563EB";

export type KollectiviaMarkAnimate = "entrance" | "idle" | "all";

export type KollectiviaMarkProps = {
  readonly size?: number;
  readonly variant?: "color" | "mono";
  /** Halo glow autour des éléments (cf. source SVG). Désactivable pour favicon/perf. */
  readonly glow?: boolean;
  readonly className?: string;
  readonly ariaLabel?: string;
  /**
   * 9.P.1b — Animation runtime. Absent = fallback static (compat 9.P.1).
   * Toutes les classes sont gated par `@media (prefers-reduced-motion: no-preference)`
   * dans `index.css` → fallback static automatique en mode reduce.
   */
  readonly animate?: KollectiviaMarkAnimate;
};

// Delays staggered pré-calculés (parité HTML reference §A+B). Tableaux
// littéraux pour éviter les imprécisions floating-point cumulatives JS
// (ex. 2.4 + 4 * 0.3 → 3.5999999999... au lieu de 3.6).
const LINE_DELAYS_S = [0, 0.15, 0.3, 0.45, 0.6, 0.75] as const;
const POP_DELAYS_S = [0.9, 1, 1.1, 1.2, 1.3, 1.4] as const;
const BREATHE_DELAYS_S = [2.4, 2.7, 3, 3.3, 3.6, 3.9] as const;

function lineClass(animate?: KollectiviaMarkAnimate): string {
  return animate === "entrance" || animate === "all" ? "anim-mark-line" : "";
}

function nodeClass(animate?: KollectiviaMarkAnimate): string {
  switch (animate) {
    case "entrance":
      return "anim-mark-node-entrance";
    case "idle":
      return "anim-mark-node-idle";
    case "all":
      return "anim-mark-node-all";
    default:
      return "";
  }
}

function svgClass(animate?: KollectiviaMarkAnimate): string {
  return animate === "idle" || animate === "all" ? "anim-mark-float" : "";
}

function lineStyle(
  animate: KollectiviaMarkAnimate | undefined,
  i: number,
): CSSProperties | undefined {
  if (animate !== "entrance" && animate !== "all") return undefined;
  return { ["--mark-line-delay" as string]: `${LINE_DELAYS_S[i] ?? 0}s` };
}

function nodeStyle(
  animate: KollectiviaMarkAnimate | undefined,
  i: number,
): CSSProperties | undefined {
  if (!animate) return undefined;
  const style: Record<string, string> = {};
  if (animate === "entrance" || animate === "all") {
    style["--mark-pop-delay"] = `${POP_DELAYS_S[i] ?? 0}s`;
  }
  if (animate === "idle" || animate === "all") {
    style["--mark-breathe-delay"] = `${BREATHE_DELAYS_S[i] ?? 0}s`;
  }
  return style as CSSProperties;
}

export function KollectiviaMark({
  size = 24,
  variant = "color",
  glow = true,
  className,
  ariaLabel,
  animate,
}: KollectiviaMarkProps): JSX.Element {
  const uid = useId().replace(/:/g, "-"); // useId() retourne ":r0:" ; SVG IDs n'aiment pas ":"
  const filterId = `glow-${uid}`;
  const g1Id = `lg1-${uid}`;
  const g2Id = `lg2-${uid}`;

  const isMono = variant === "mono";
  const a11y: Pick<SVGProps<SVGSVGElement>, "role" | "aria-label" | "aria-hidden"> =
    ariaLabel ? { role: "img", "aria-label": ariaLabel } : { "aria-hidden": true };

  const filterAttr = glow ? `url(#${filterId})` : undefined;

  function lineStroke(line: LineSpec): string {
    if (isMono) return MONO_STROKE;
    if ("gradient" in line) {
      return line.gradient === "g1" ? `url(#${g1Id})` : `url(#${g2Id})`;
    }
    return line.stroke;
  }

  const svgFloatClass = svgClass(animate);
  const svgClassName = [className, svgFloatClass].filter(Boolean).join(" ") || undefined;
  const lineCls = lineClass(animate);
  const nodeCls = nodeClass(animate);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={svgClassName}
      {...a11y}
    >
      <defs>
        {glow && (
          <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0.2  0 0 0 0 0.45  0 0 0 0 1  0 0 0 0.75 0"
            />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
        {!isMono && (
          <>
            <linearGradient id={g1Id} x1="120" y1="62" x2="390" y2="438" gradientUnits="userSpaceOnUse">
              <stop stopColor="#1D7BEF" />
              <stop offset="0.55" stopColor="#22AEEB" />
              <stop offset="1" stopColor="#7B32EA" />
            </linearGradient>
            <linearGradient id={g2Id} x1="120" y1="438" x2="390" y2="62" gradientUnits="userSpaceOnUse">
              <stop stopColor="#246EEB" />
              <stop offset="0.5" stopColor="#22AEEB" />
              <stop offset="1" stopColor="#7B32EA" />
            </linearGradient>
          </>
        )}
      </defs>

      {/* Lines (6 segments) */}
      <g
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={14}
        fill="none"
        {...(filterAttr ? { filter: filterAttr } : {})}
      >
        {LINES.map((line, i) => {
          const a = NODES[line.from]!;
          const b = NODES[line.to]!;
          return (
            <line
              key={`l-${i}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={lineStroke(line)}
              className={lineCls || undefined}
              style={lineStyle(animate, i)}
            />
          );
        })}
      </g>

      {/* Nodes (6 cercles, radii variables, fills spécifiques) */}
      <g {...(filterAttr ? { filter: filterAttr } : {})}>
        {NODES.map((n, i) => (
          <circle
            key={`n-${i}`}
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill={isMono ? MONO_FILL : n.fill}
            className={nodeCls || undefined}
            style={nodeStyle(animate, i)}
          />
        ))}
      </g>
    </svg>
  );
}

export type KollectiviaWordmarkLayout = "horizontal" | "vertical";

export type KollectiviaWordmarkProps = {
  readonly markSize?: number;
  readonly className?: string;
  /** "dark" (slate-900 sur fond clair) ou "light" (white sur fond sombre type navy-950). */
  readonly tone?: "dark" | "light";
  /** Taille texte (px). Défaut 15. ~24-32 pour usages hero. */
  readonly textSize?: number;
  /** Glow halo autour du mark. Défaut true. False pour contextes perf/petit. */
  readonly glow?: boolean;
  /**
   * 9.P.1b — Animation runtime forwardée au mark interne. Absent = static.
   */
  readonly animate?: KollectiviaMarkAnimate;
  /**
   * 9.P.1b — Composition responsive :
   *   - "horizontal" (défaut) : mark + texte côte-à-côte (état actuel, compat 9.P.1).
   *   - "vertical"            : mark dessus, texte dessous (espaces étroits / mobile portrait).
   * Pour switch responsive automatique, le caller monte 2 instances avec
   * Tailwind `className="hidden md:inline-flex"` / `className="md:hidden"`.
   */
  readonly layout?: KollectiviaWordmarkLayout;
};

/**
 * Wordmark complet — mark + texte « Kollectivia » avec « ia » accent.
 *
 * Utilisé dans :
 *   - `IntakeChrome` top-bar (chunk 9.N.2 — tone="dark" markSize=22)
 *   - `LoginPage` panel gauche hero (chunk 9.O.1 — tone="light" markSize=56 textSize=24)
 *   - 9.P.1b — same hero avec `animate="all"` (entrance + idle)
 *
 * `translate="no"` sur fragments texte → empêche les auto-traducteurs (Chrome
 * translate, Google Translate widget) de casser la marque (« Kollectivia » →
 * « Collectivia », etc.) — cf. WIG locale §i18n.
 */
export function KollectiviaWordmark({
  markSize = 22,
  className,
  tone = "dark",
  textSize = 15,
  glow = true,
  animate,
  layout = "horizontal",
}: KollectiviaWordmarkProps): JSX.Element {
  const baseColor = tone === "light" ? "text-white" : "text-slate-900";
  const accentColor = tone === "light" ? "text-brand-300" : "text-brand-600";
  const layoutClasses =
    layout === "vertical"
      ? "inline-flex flex-col items-center gap-1.5"
      : "inline-flex items-center gap-2";

  return (
    <span
      className={[layoutClasses, className].filter(Boolean).join(" ")}
      translate="no"
    >
      <KollectiviaMark
        size={markSize}
        ariaLabel="Kollectivia"
        glow={glow}
        animate={animate}
      />
      <span
        className={`font-semibold tracking-tight ${baseColor}`}
        style={{ fontSize: `${textSize}px` }}
      >
        <span>Kollectiv</span>
        <span className={accentColor}>ia</span>
      </span>
    </span>
  );
}
